import React, { useEffect, useMemo, useState } from 'react';
import '../../css/modal.css';
import { Query } from '../../types/Query';
import { createBookingFromQuery } from '../../api/Booking';
import { fetchBookingDrafts, materializeBookingDraft } from '../../api/BookingDraft';
import { fetchPackages } from '../../api/Packages';
import { TravelPackage } from '../../types/TravelPackage';
import { buildBookingComponentsSummary } from '../../utils/bookingComponents';
import { formatDisplayDate } from '../../utils/bookingComponents';
import BookingComponentTables from './BookingComponentTables';

interface Props {
  query: Query;
  onClose: () => void;
  onCreated: (alreadyMaterialized?: boolean) => void;
}

type PassengerCounts = { adults: number; children: number };

const toPaise = (value: string, label: string) => {
  const normalized = value.trim();
  const match = /^(0|[1-9]\d*)(?:\.(\d{1,2}))?$/.exec(normalized);
  if (!match) throw new Error(`${label} must be a non-negative amount with at most two decimal places`);
  const whole = Number(match[1]);
  const fraction = Number(`${match[2] || ''}00`.slice(0, 2));
  const paise = whole * 100 + fraction;
  if (!Number.isSafeInteger(paise)) throw new Error(`${label} is too large`);
  return paise;
};

const fromPaise = (value: unknown) => {
  const amount = Number(value || 0) / 100;
  return Number.isFinite(amount) ? amount.toFixed(2) : '';
};

const dateOnly = (value: unknown) => {
  const text = String(value || '');
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
};

const derivePlanTravelDates = (plan: unknown) => {
  const summary = buildBookingComponentsSummary({ bookingPlan: plan as any });
  if (!summary) return { start: '', end: '' };
  const startDates = [
    summary.travelStartDate,
    ...summary.hotels.map((hotel) => hotel.checkInDate),
    ...summary.flights.map((flight) => flight.departureTime),
    ...summary.transfers.map((transfer) => transfer.transferDate),
    ...summary.activities.map((activity) => activity.serviceDate),
    ...summary.insurance.map((item) => item.coverageStartDate),
  ].map(dateOnly).filter(Boolean).sort();
  const endDates = [
    summary.travelEndDate,
    ...summary.hotels.map((hotel) => hotel.checkOutDate),
    ...summary.flights.map((flight) => flight.arrivalTime),
    ...summary.transfers.map((transfer) => transfer.transferDate),
    ...summary.activities.map((activity) => activity.serviceDate),
    ...summary.insurance.map((item) => item.coverageEndDate),
  ].map(dateOnly).filter(Boolean).sort();
  return { start: startDates[0] || '', end: endDates[endDates.length - 1] || '' };
};

const asPositiveAmount = (value: unknown) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const estimateDraftPayable = (draft: any) => {
  const quotation = draft?.provider_ref || draft?.booking_plan?.quotation || {};
  const breakdown = quotation?.price?.breakdown || quotation?.priceBreakdown || {};
  const subtotal = asPositiveAmount(breakdown.subtotal);
  if (subtotal) return subtotal;

  const categoryTotal = ['flights', 'hotels', 'activities', 'transfers', 'addons', 'visa', 'insurance']
    .reduce((sum, key) => sum + asPositiveAmount(breakdown[key]), 0);
  if (categoryTotal) return categoryTotal;

  const planSummary = buildBookingComponentsSummary({ bookingPlan: draft?.booking_plan });
  if (!planSummary) return 0;
  return [
    ...planSummary.hotels.map((hotel) => hotel.price),
    ...planSummary.flights.map((flight) => flight.price),
  ].reduce<number>((sum, amount) => sum + asPositiveAmount(amount), 0);
};

const AddBookingModal: React.FC<Props> = ({ query, onClose, onCreated }) => {
  const [travelStartDate, setTravelStartDate] = useState<string>('');
  const [travelEndDate, setTravelEndDate] = useState<string>('');
  const [packages, setPackages] = useState<TravelPackage[]>([]);
  const [packageId, setPackageId] = useState('');
  const [notes, setNotes] = useState<string>('');
  const [totalAmountReceivable, setTotalAmountReceivable] = useState<string>('');
  const [totalAmountPayable, setTotalAmountPayable] = useState<string>('');
  const [existingDraft, setExistingDraft] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [packagesLoading, setPackagesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPackages = async () => {
      setPackagesLoading(true);
      try {
        const result = await fetchPackages();
        if (cancelled) return;
        setPackages(Array.isArray(result) ? result : []);
      } catch (loadError: any) {
        if (cancelled) return;
        setError(loadError?.message || 'Failed to load packages');
      } finally {
        if (!cancelled) setPackagesLoading(false);
      }
    };

    void loadPackages();

    return () => {
      cancelled = true;
    };
  }, [query.id]);

  useEffect(() => {
    let cancelled = false;
    void fetchBookingDrafts(query.id).then((drafts) => {
      if (cancelled) return;
      const draft = drafts[0];
      if (!draft) return;
      const derivedDates = derivePlanTravelDates(draft.booking_plan);
      setExistingDraft(draft);
      setPackageId(String(draft.package_id || ''));
      setTravelStartDate(String(draft.start_at || draft.booking_plan?.summary?.travelStartDate || derivedDates.start));
      setTravelEndDate(String(draft.end_at || draft.booking_plan?.summary?.travelEndDate || derivedDates.end));
      setTotalAmountReceivable(fromPaise(draft.total_amount_receivable_paise));
      const recordedPayablePaise = Number(draft.total_amount_payable_paise || 0);
      const calculatedPayable = recordedPayablePaise > 0 ? recordedPayablePaise / 100 : estimateDraftPayable(draft);
      setTotalAmountPayable(calculatedPayable ? calculatedPayable.toFixed(2) : '');
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [query.id]);

  const passengers = useMemo<PassengerCounts>(() => (
    (query.rooms || []).reduce<PassengerCounts>((total, room) => ({
      adults: total.adults + Number(room.Adults ?? room.adults ?? 0),
      children: total.children + Number(room.Children ?? room.children ?? 0),
    }), { adults: 0, children: 0 })
  ), [query.rooms]);

  const bookingPlanSummary = useMemo(() => (
    existingDraft ? buildBookingComponentsSummary({
      bookingPlan: existingDraft.booking_plan,
      travelStartDate,
      travelEndDate,
      totalAmount: Number(existingDraft.total_amount_receivable_paise || 0) / 100,
    }) : null
  ), [existingDraft, travelEndDate, travelStartDate]);

  const draftPackageName = existingDraft?.package_name
    || packages.find((pkg) => String(pkg.id) === String(existingDraft?.package_id || ''))?.title
    || 'Package quotation';

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      const customerId = query.customerId ?? query.customer?.id;
      if (!customerId) throw new Error('A customer is required before creating an order');
      const totalAmountReceivablePaise = toPaise(totalAmountReceivable, 'Total amount receivable');
      const totalAmountPayablePaise = toPaise(totalAmountPayable, 'Total amount payable');
      if (existingDraft) {
        await materializeBookingDraft(existingDraft.id, { totalAmountReceivablePaise, totalAmountPayablePaise });
        onCreated(true);
      } else {
        await createBookingFromQuery(query.id, {
          packageId,
          customerId,
          travelStartDate,
          travelEndDate,
          notes,
          passengers: { adults: Math.max(1, passengers.adults), children: passengers.children },
          totalAmountReceivablePaise,
          totalAmountPayablePaise,
        });
        onCreated(false);
      }
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg add-booking-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Booking</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {packagesLoading && (
            <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
              Loading packages…
            </div>
          )}
          <div style={{ marginBottom: 8, fontSize: 14, color: '#374151' }}>
            <strong>Customer:</strong> {query.customer?.name || '—'} · <strong>Type:</strong> {query.type} · <strong>Location:</strong> {query.location}
          </div>
          {existingDraft && (
            <>
              <div style={{ marginBottom: 8, fontSize: 13, color: '#1d4ed8' }}>
                Confirming the quotation draft. Its quoted package and travel dates are retained.
              </div>
              <div className="booking-draft-summary">
                <div className="booking-draft-summary__header">
                  <div>
                    <div className="booking-draft-summary__title">{draftPackageName}</div>
                    <div className="booking-draft-summary__subtitle">Quotation draft booking plan</div>
                  </div>
                  <div className="booking-draft-summary__amount">₹{Number(totalAmountReceivable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                <div className="booking-draft-summary__facts">
                  <div className="booking-draft-summary__fact"><span className="booking-draft-summary__label">Travel start</span><strong>{formatDisplayDate(travelStartDate)}</strong></div>
                  <div className="booking-draft-summary__fact"><span className="booking-draft-summary__label">Travel end</span><strong>{formatDisplayDate(travelEndDate)}</strong></div>
                  <div className="booking-draft-summary__fact"><span className="booking-draft-summary__label">Receivable</span><strong>₹{Number(totalAmountReceivable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                  <div className="booking-draft-summary__fact"><span className="booking-draft-summary__label">Payable</span><strong>₹{Number(totalAmountPayable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                </div>
                <BookingComponentTables
                  summary={bookingPlanSummary}
                  emptyText="No component details were recorded in this quotation draft"
                />
              </div>
            </>
          )}
          <div className="add-booking-modal__settlement-note">
            {existingDraft
              ? 'Review and update the final receivable and supplier payable amounts before confirming this booking.'
              : 'Enter the agreed customer receivable amount and estimated supplier payable cost. Both values are retained in the package-order audit record.'}
          </div>
          {!existingDraft && (
            <label>
              Package (optional for a manual booking)
              <select aria-label="Package" value={packageId} onChange={e => setPackageId(e.target.value)} disabled={packagesLoading}>
                <option value="">No package — manual booking</option>
                {packages.map((pkg) => <option key={pkg.id} value={pkg.id}>{pkg.title}</option>)}
              </select>
            </label>
          )}
          {!existingDraft && (
            <>
              <label>
                Travel Start Date
                <input
                  aria-label="Travel Start Date"
                  type="date"
                  value={travelStartDate}
                  onChange={e => setTravelStartDate(e.target.value)}
                />
              </label>
              <label>
                Travel End Date
                <input
                  aria-label="Travel End Date"
                  type="date"
                  value={travelEndDate}
                  onChange={e => setTravelEndDate(e.target.value)}
                />
              </label>
            </>
          )}
          <label>
            Total Amount Receivable (₹)
            <input
              aria-label="Total Amount Receivable (₹)"
              type="text"
              inputMode="decimal"
              value={totalAmountReceivable}
              onChange={e => setTotalAmountReceivable(e.target.value)}
              placeholder="e.g. 55000"
              required
            />
          </label>
          <label>
            Total Amount Payable (₹)
            <input
              aria-label="Total Amount Payable (₹)"
              type="text"
              inputMode="decimal"
              value={totalAmountPayable}
              onChange={e => setTotalAmountPayable(e.target.value)}
              placeholder="e.g. 42000"
              required
            />
          </label>
          <div style={{ marginBottom: 12, fontSize: 13, color: '#374151' }}>
            Expected markup: <strong>{(() => {
              try {
                const markup = toPaise(totalAmountReceivable, 'Total amount receivable') - toPaise(totalAmountPayable, 'Total amount payable');
                return `₹${(markup / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              } catch {
                return 'enter both amounts';
              }
            })()}</strong>
          </div>
          {!existingDraft && (
            <label>
              Notes
              <textarea value={notes} onChange={e => setNotes(e.target.value)} />
            </label>
          )}
          {error && <div style={{ color: '#b91c1c', fontSize: 12 }}>{error}</div>}
        </div>
        <div className="modal-actions">
          <button type="button" className="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : existingDraft ? 'Confirm Booking' : 'Create Booking'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBookingModal;
